import { useCallback, useRef } from 'react';
import { Destination, DestinationObject, MappingMode, Source } from '../types/DataSyncItem';
import { DataSyncMappingService } from '../services/DataSync/DataSyncMappingService';
import { DataSyncObjectsStore } from '../stores/object/DataSyncObjectsStore';
import DataSyncCgObjectsStore from '../stores/object/DataSyncCgObjectsStore';
import { FormAction, FormActionType } from '../pages/DataSync/components/formReducer';
import { filterSystemFields } from '../lib/dataSync';
import { FieldDefinition } from '../types/FieldDefinition';
import { DataSyncViewModel } from '../types/DataSyncViewModel';

export function useDataSyncActions(
  viewModel: DataSyncViewModel,
  destinationFields: FieldDefinition[],
  fieldMappingMode: MappingMode | undefined,
  dispatch: React.Dispatch<FormAction>,
  sourceStore: DataSyncObjectsStore | null,
  destinationStore: DataSyncObjectsStore | null,
) {
  // Refs to avoid stale closures in async callbacks.
  // After an await, the values captured in the useCallback closure may be outdated
  // because React may have re-rendered with new state. Reading from refs ensures
  // we always see the latest values.
  const viewModelRef = useRef(viewModel);
  viewModelRef.current = viewModel;
  const destinationFieldsRef = useRef(destinationFields);
  destinationFieldsRef.current = destinationFields;
  const fieldMappingModeRef = useRef(fieldMappingMode);
  fieldMappingModeRef.current = fieldMappingMode;
  // The Graph destination listing is scoped to source identity (product + instance), not the
  // source object, so only an identity change should clear the destination cache.
  const lastSourceIdentityRef = useRef<string>(
    `${viewModel?.source?.product ?? ''}::${viewModel?.source?.instance?.id ?? ''}`
  );

  const resetFixedMappingMode = useCallback(() => {
    if (fieldMappingModeRef.current === MappingMode.FIXED) {
      destinationStore?.setTransientDestinationObject(undefined);
      dispatch({ type: FormActionType.RESET_FIXED_MODE });
    }
  }, [destinationStore, dispatch]);

  const inheritSourceSchema = useCallback(async (
    newDestination: Destination,
    baseType?: string,
  ) => {
    const updatedFieldMappings = await DataSyncMappingService.inheritSourceSchema(
      viewModelRef.current.source,
      newDestination.object.object_name,
      sourceStore,
      destinationStore,
      baseType,
    );

    const destinationObject = destinationStore?.getTransientDestinationObject();
    const destFields: FieldDefinition[] = destinationObject?.fields
      ? filterSystemFields(destinationObject.fields)
      : destinationFieldsRef.current;

    const sortedMappings = updatedFieldMappings.sort((a, b) =>
      a.sourceField.localeCompare(b.sourceField)
    );
    dispatch({
      type: FormActionType.INHERIT_SCHEMA,
      destination: newDestination,
      mappings: sortedMappings,
      destinationFields: destFields,
    });
  }, [sourceStore, destinationStore, dispatch]);

  const onSourceChange = useCallback(async (newSource: Source) => {
    let fetchedFields: FieldDefinition[] = [];
    if (sourceStore) {
      try {
        fetchedFields = filterSystemFields(
          await sourceStore.getSourceObjectFields(newSource.object.object_name)
        );
      } catch {
        fetchedFields = [];
      }
    }

    dispatch({
      type: FormActionType.SOURCE_CHANGED,
      source: newSource,
      sourceFields: fetchedFields,
    });

    const newSourceIdentity = `${newSource?.product ?? ''}::${newSource?.instance?.id ?? ''}`;
    if (
      destinationStore instanceof DataSyncCgObjectsStore &&
      newSourceIdentity !== lastSourceIdentityRef.current
    ) {
      destinationStore.clearDestinationCache();
    }
    lastSourceIdentityRef.current = newSourceIdentity;

    if (fieldMappingModeRef.current === MappingMode.FIXED) {
      // The destination object was inherited from the previous source. The
      // inheritance is now broken, so clear the destination object -- otherwise
      // the dropdown shows a stale name that the availability check flags as
      // "no longer available" once the transient destination object is dropped.
      dispatch({
        type: FormActionType.DESTINATION_CHANGED,
        destination: {
          ...viewModelRef.current.destination,
          object: {} as DestinationObject,
        },
        destinationFields: [],
      });
      resetFixedMappingMode();
    } else {
      try {
        const isInherited = await DataSyncMappingService.isCGDestinationInheritedFromSource(
          newSource, viewModelRef.current.destination, sourceStore, destinationStore,
        );
        if (isInherited) {
          await inheritSourceSchema(viewModelRef.current.destination);
        }
      } catch {
        // If the inherited check fails, leave mapping mode unchanged
      }
    }
  }, [sourceStore, destinationStore, dispatch, resetFixedMappingMode, inheritSourceSchema]);

  const onDestinationChange = useCallback(async (newDestination: Destination) => {
    if (!newDestination?.object?.object_name) {
      dispatch({
        type: FormActionType.DESTINATION_CHANGED,
        destination: newDestination,
        destinationFields: [],
      });
      return;
    }

    if (viewModelRef.current.destination.object.object_name === newDestination.object.object_name) {
      dispatch({
        type: FormActionType.DESTINATION_CHANGED,
        destination: newDestination,
        destinationFields: destinationFieldsRef.current,
      });
      return;
    }

    // Dispatch immediately so UI reflects the selection
    dispatch({
      type: FormActionType.DESTINATION_CHANGED,
      destination: newDestination,
      destinationFields: [],
    });

    try {
      const isInherited = await DataSyncMappingService.isCGDestinationInheritedFromSource(
        viewModelRef.current.source, newDestination, sourceStore, destinationStore,
      );

      if (isInherited) {
        await inheritSourceSchema(newDestination);
      } else {
        if (fieldMappingModeRef.current === MappingMode.FIXED) {
          resetFixedMappingMode();
        }
        try {
          const fields = await destinationStore?.getDestinationObjectFields(
            newDestination.object.object_name,
          );
          dispatch({
            type: FormActionType.SET_DESTINATION_FIELDS,
            destinationFields: filterSystemFields(fields || []),
          });
        } catch {
          dispatch({ type: FormActionType.SET_DESTINATION_FIELDS, destinationFields: [] });
        }
      }
    } catch {
      if (fieldMappingModeRef.current === MappingMode.FIXED) {
        resetFixedMappingMode();
      }
      dispatch({ type: FormActionType.SET_DESTINATION_FIELDS, destinationFields: [] });
    }
  }, [sourceStore, destinationStore, dispatch, resetFixedMappingMode, inheritSourceSchema]);

  return { onSourceChange, onDestinationChange, inheritSourceSchema, resetFixedMappingMode };
}
